import Konva from 'konva';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Text, Rect as Rectangle, Ring, Path, Group } from 'react-konva';
import { baseTheme } from 'styles/utils';
import { useShapeText } from 'utils/konva/useShapeText';
import { setCursor } from 'components/Canvas/helpers';
import { CHECK_MARK_OFFSET } from 'components/Canvas/Rect/Rect';

const LABEL_PADDING = 6;
const RING_RADIUS = 3;
const RECT_TAG_GAP = 4;

const cornerStyleMap = {
  rounded: [5, 5, 5, 5],
  'rounded-top': [5, 5, 0, 0],
};

export interface Label {
  name: string;
  id?: string;
  value?: string;
  color?: string;
}

export enum LabelOrientationEnum {
  BOTTOM,
  RIGHT,
}

export interface RegionLabelProps {
  label: Label;
  scale?: number;
  color?: string;
  opacity?: number;
  x: number;
  y: number;
  height?: number;
  onMouseEnter?: (evt: Konva.KonvaEventObject<MouseEvent>) => void;
  onMouseLeave?: (evt: Konva.KonvaEventObject<MouseEvent>) => void;
  onAcceptButtonClick?: (label: Label) => void;
  cornerStyle?: keyof typeof cornerStyleMap;
  orientation?: LabelOrientationEnum;
  isPredicted?: boolean;
  fillEnabled?: boolean;
  fillPatternImage?: HTMLImageElement;
}

// The first number corresponds to the width of the shape and the second one to the height.
type ShapeSize = [number, number];

export const RegionLabel = forwardRef<Konva.Group, RegionLabelProps & { width: number }>(
  (
    {
      label,
      color = baseTheme.light.colors.black,
      x,
      y,
      width,
      height = 30,
      scale = 1,
      opacity,
      onMouseEnter,
      onMouseLeave,
      onAcceptButtonClick,
      cornerStyle = 'rounded-top',
      orientation = LabelOrientationEnum.BOTTOM,
      isPredicted,
    },
    ref,
  ) => {
    const rectRef = useRef<Konva.Rect>(null);
    const ringRef = useRef<Konva.Ring>(null);

    const [textNode, setTextNode] = useState<Konva.Text | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [[_, textHeight], setTextSize] = useState<ShapeSize>([0, 0]);
    const [checkNode, setCheckNode] = useState<Konva.Path | null>(null);

    const actualFontSize = 11;
    const { name: labelName, value: labelValue } = label;

    const [labelText, textRectSize] = useShapeText({
      label: labelValue ? `${labelName} | ${labelValue}` : labelName,
      isSelected: true,
      textNode,
      labelStyle: {
        fontSize: actualFontSize,
        padding: LABEL_PADDING,
      },
      containerDimensions: {
        width,
        height,
      },
      scale,
      isPredicted,
    });

    const textRef = useCallback((node: Konva.Text | null) => {
      if (node) {
        setTextNode(node);
        setTextSize([node.width(), node.height()]);
      }
    }, []);

    const checkRef = useCallback((node: Konva.Path | null) => {
      if (node) {
        setCheckNode(node);
      }
    }, []);

    const handleMouseEnter = useCallback((evt: Konva.KonvaEventObject<MouseEvent>) => {
      onMouseEnter?.(evt);
    }, []);

    const handleMouseLeave = useCallback((evt: Konva.KonvaEventObject<MouseEvent>) => {
      onMouseLeave?.(evt);
    }, []);

    useEffect(() => {
      /**
       * The last part is necessary, because in Jest tests, react-konva renders <div> elements for the components,
       * which don't have the konva instance methods like `moveToTop()`. Thus, we skip the body of this hook
       * during test execution...
       * https://github.com/konvajs/react-konva/issues/419
       */
      if (textNode && ringRef.current && rectRef.current && typeof rectRef.current.moveToTop === 'function') {
        rectRef.current.moveToTop();
        textNode.moveToTop();
        ringRef.current.moveToTop();
      }
    }, [textNode]);

    const rectWidth = (textRectSize[0] + LABEL_PADDING / 2 / scale) / scale;
    const rectHeight = textRectSize[1] / scale;
    const yWithOffset = y + RECT_TAG_GAP / scale;

    const orientationShapeMap = {
      [LabelOrientationEnum.BOTTOM]: {
        rectangle: [x, yWithOffset],
        ring: [x + LABEL_PADDING / 3 / scale + RING_RADIUS * (2 / scale), yWithOffset + rectHeight / 2],
        text: [x + RING_RADIUS * (2.5 / scale), yWithOffset + rectHeight / 2 - textHeight / scale / 2],
      },
      [LabelOrientationEnum.RIGHT]: {
        rectangle: [Math.max(x + (RECT_TAG_GAP * 2) / scale, 0), Math.max(y - textRectSize[1] / scale, 0)],
        ring: [Math.max(x + 10 / scale + (RECT_TAG_GAP + RING_RADIUS) / scale, 0), Math.max(y - (textRectSize[1] / scale) * 0.5, 0)],
        text: [Math.max(x + (RECT_TAG_GAP + RING_RADIUS * 2 + LABEL_PADDING) / scale, 0), Math.max(y - textRectSize[1] / scale - 1 / scale, 0)],
      },
    };

    const orientationMap = orientationShapeMap[orientation];

    const [rectX, rectY] = orientationMap.rectangle;
    const [ringX, ringY] = orientationMap.ring;
    const [textX, textY] = orientationMap.text;
    const checkX = rectX + rectWidth - CHECK_MARK_OFFSET / scale;
    const checkY = y + rectHeight / 2 - (checkNode ? checkNode.getClientRect().height / 4 / scale : 0);

    const rectangleFill = isPredicted ? baseTheme.light.colors.white : label.color || color;
    const rectangleStroke = label.color || color;

    const ringFill = isPredicted ? label.color || color : baseTheme.light.colors.white;

    const textFill = isPredicted ? label.color || color : baseTheme.light.colors.white;

    return (
      <>
        <Group ref={ref}>
          <Rectangle
            ref={rectRef}
            x={rectX}
            y={rectY}
            z={998}
            width={rectWidth}
            height={rectHeight}
            opacity={opacity}
            {...(isPredicted && {
              stroke: rectangleStroke,
              strokeWidth: 1,
            })}
            fill={rectangleFill}
            cornerRadius={cornerStyleMap[cornerStyle].map((radius) => radius / scale)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          <Ring ref={ringRef} x={ringX} y={ringY} innerRadius={0} outerRadius={RING_RADIUS / scale} fill={ringFill} opacity={opacity} />
          <Text
            ref={textRef}
            x={textX}
            y={textY}
            align="left"
            fontFamily="Inter"
            fontVariant="bold"
            opacity={opacity}
            fill={textFill}
            text={labelText}
            fontSize={actualFontSize}
            padding={LABEL_PADDING}
            scaleX={1 / scale}
            scaleY={1 / scale}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        </Group>
        {isPredicted && (
          <Group
            onMouseEnter={(e) => setCursor(e, 'pointer')}
            onMouseLeave={(e) => setCursor(e, 'auto')}
            onClick={() => onAcceptButtonClick?.(label)}
          >
            {/* Ghost element to facilitate the process of clicking the icon. */}
            <Rectangle width={45} height={rectHeight} x={checkX} y={yWithOffset} fill="transparent" />
            <Path
              ref={checkRef}
              x={checkX}
              y={checkY}
              scaleX={1 / scale}
              scaleY={1 / scale}
              opacity={opacity}
              fill="transparent"
              data="M12.3333 1L4.99999 8.33333L1.66666 5"
              stroke={label.color || color}
            />
          </Group>
        )}
      </>
    );
  },
);
